// pages/sections/StaleClientSection.js
// Partner-only review of stale clients (ContIndex PK). Two toggles per row:
//   * Mark reviewed (required)
//   * Mark NLC (optional — writes to nlc_clients, excludes client from every report, this list included)
// OpenJobs and BillingGroupClients arrive as pre-folded arrays from the API
// and are surfaced as hover popovers.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { useAuth } from '../../auth/AuthContext';
import { usePeriod } from '../../hooks/usePeriod';
import { useSectionData } from '../../hooks/useSectionData';
import { useRowMutations } from '../../hooks/useRowMutations';
import LastRefreshed from '../../components/LastRefreshed';
import ToggleSwitch from './ToggleSwitch';
import DisengagementModal from './DisengagementModal';
import { nlcModalProps, sweepRelated } from './nlcRequestUi';
import { readError, sortDoneLast } from './reviewUtil';
import { fmtDate } from './reviewUtil';
import './ReviewTable.css';

const PAGE_SIZE = 25;
const USD = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

const GROUP_VIEWS = [
  { key: 'client',  label: 'Individual' },
  { key: 'billing', label: 'Billing Groups' },
  { key: 'relationship', label: 'Relationship Groups' },
];

// A row's group anchor (client code) for the chosen view; solo clients anchor to themselves.
function anchorFor(row, groupBy) {
  if (groupBy === 'relationship') return row.relationshipGroup || row.clientCode;
  if (groupBy === 'billing') return row.billingGroupKey || row.clientCode;
  return row.clientCode;
}

const num = v => Number(String(v ?? 0).replace(/[$,]/g, '')) || 0;

// The group shown in the group column: the relationship group in Relationship view, else the billing
// group (Individual and Billing views). `clients` is the whole group, stale or not, so the count and
// popover match the totals.
function groupColumn(row, groupBy) {
  return groupBy === 'relationship'
    ? { label: row.relationshipGroupName, clients: row.relationshipGroupClients || [] }
    : { label: row.billingGroup,    clients: row.billingGroupClients || [] };
}

// Roll rows up into one entity per client (Individual) or per group anchor.
function buildEntities(rows, groupBy) {
  if (groupBy === 'client') {
    return rows.map(r => ({
      key: String(r.contIndex), code: r.clientCode, name: r.clientName, office: r.clientOffice,
      manager: r.clientManager, wip: num(r.wipLast365), billed: num(r.billedLast365),
      openJobs: r.openJobs, members: [r], isGroup: false, group: groupColumn(r, groupBy),
    }));
  }
  // Group WIP / billed cover EVERY client in the group (computed server-side), not just the
  // stale ones listed here; all of an entity's members share the same totals. null = unavailable.
  const wipKey    = groupBy === 'relationship' ? 'relationshipGroupWip365'    : 'billingGroupWip365';
  const billedKey = groupBy === 'relationship' ? 'relationshipGroupBilled365' : 'billingGroupBilled365';
  const buckets = new Map();
  for (const r of rows) {
    const a = anchorFor(r, groupBy);
    if (!buckets.has(a)) buckets.set(a, []);
    buckets.get(a).push(r);
  }
  return [...buckets].map(([anchor, rawMembers]) => {
    // The Code column always shows a client's own code (as in Individual view), never the group's anchor
    // code: the lead is the group's anchor when it is listed here, else its lowest-coded stale member.
    const members = [...rawMembers].sort((a, b) => String(a.clientCode).localeCompare(String(b.clientCode), undefined, { numeric: true }));
    const lead = members.find(m => m.clientCode === anchor) || members[0];
    const named = groupBy === 'relationship' ? lead.relationshipGroupName : lead.billingGroupName;
    return {
      key: anchor, code: lead.clientCode, name: members.length > 1 ? (named || lead.clientName) : lead.clientName,
      office: lead.clientOffice, manager: lead.clientManager,
      wip: lead[wipKey] ?? null,
      billed: lead[billedKey] ?? null,
      openJobs: [...new Set(members.flatMap(m => m.openJobs))],
      members, lead, isGroup: members.length > 1, group: groupColumn(lead, groupBy),
    };
  });
}

// "N jobs" with a hover popover listing them (shared by Individual, group and member rows).
function JobsCell({ jobs }) {
  if (!jobs.length) return <span className="rt-cell-muted">—</span>;
  return (
    <span className="rt-list-cell">
      <span className="rt-list-preview">{jobs.length} job{jobs.length !== 1 ? 's' : ''}</span>
      <span className="rt-list-popover">
        <ul>{jobs.map((j, i) => <li key={i}>{j}</li>)}</ul>
      </span>
    </span>
  );
}

// "Name (n)" with a hover popover listing the group's clients; a solo client shows just the name.
function GroupCell({ label, clients }) {
  if (clients.length <= 1) return <span className="rt-cell-muted">{label || '—'}</span>;
  return (
    <span className="rt-list-cell">
      <span className="rt-list-preview">{label} ({clients.length})</span>
      <span className="rt-list-popover">
        <ul>{clients.map((c, i) => <li key={i}>{c.code} — {c.name}</li>)}</ul>
      </span>
    </span>
  );
}

const money = v => (v == null ? '—' : USD.format(v));

// Header tooltips. A client is only listed when it has had no time for 18 months, so its own
// WIP / Billed 365 are always $0 — the subtle date under each is the context that matters.
const WIP_TIP = 'Value of time entered on the client in the last 365 days. A client is listed only when it '
  + 'has had no time for 18 months, so this is $0 for every client here; the date underneath is its last '
  + 'time entry ever. In the Relationship / Billing Group views the group row totals every client in the group, '
  + 'stale or not.';
const BILLED_TIP = 'Value of that time billed in the last 365 days (billed time WIP, not invoices). $0 for every '
  + 'client here for the same reason; the date underneath is the client\'s last invoice ever. In the Relationship / '
  + 'Billing Group views the group row totals every client in the group, stale or not.';

// Subtle context line under a client's WIP / Billed 365: its last time entry / last invoice ever.
// FHP's Practice Engine history starts at the merger, so an FHP client with no date has had none
// since then — not necessarily never.
function LastDate({ label, date, none, fhp }) {
  if (date) return <div className="rt-cell-sub">{`${label} ${fmtDate(date)}`}</div>;
  return fhp
    ? <div className="rt-cell-sub" title="FHP's history in Practice Engine starts at the merger (first FHP time entry 7/1/2026).">{none} since merger</div>
    : <div className="rt-cell-sub">{none} on record</div>;
}

// A group is reviewed once every member is reviewed (NLC counts as reviewed).
const memberDone = m => m.reviewed || m.isNlc;

export default function StaleClientSection() {
  const { isAdmin, email } = useAuth();
  const { period, periodActive } = usePeriod();

  const [search, setSearch]   = useState('');
  const [officeFilter, setOfficeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState(''); // '' | 'pending' | 'reviewed'
  const [page, setPage]       = useState(1);
  const [groupBy, setGroupBy] = useState('client'); // client | relationship | billing
  const [expanded, setExpanded] = useState(new Set()); // group keys with members shown
  const [nlcModalRow, setNlcModalRow] = useState(null); // row pending the NLC disengagement modal
  const [nlcSaving, setNlcSaving]     = useState(false);
  const [nlcError, setNlcError]       = useState(null);   // last failed NLC submit — shown in the modal

  const fetcher = useCallback(async () => {
    if (!period) return null;
    const res = await fetch(`/api/diStaleClients/${period}`, { credentials: 'include' });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
  }, [period]);

  // Optimistic saves: ordered per client, never disable the controls, and a refetch can't overwrite an edit.
  const mutations = useRowMutations(r => r.contIndex);
  const { pinned, clearPinned } = mutations;

  const { data, setData, initialLoading, error, lastRefreshedAt, freshness, silentRefetch }
    = useSectionData(fetcher, {
      deps: [period, email],
      heartbeat: { sections: ['stale-client-review'], period },
      reconcile: mutations.reconcile,
    });

  // Rows edited this session stay listed (e.g. under "Pending") until the user changes a filter or the view.
  useEffect(() => { clearPinned(); }, [search, officeFilter, statusFilter, groupBy, clearPinned]);

  // Optimistic row patcher. Each caller passes a transform; we apply it and
  // return the previous row so it can be restored on failure.
  function patchRow(contIndex, updater) {
    let prevRow = null;
    setData(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        rows: prev.rows.map(r => {
          if (String(r.contIndex) !== String(contIndex)) return r;
          prevRow = r;
          return updater(r);
        }),
      };
    });
    return prevRow;
  }

  async function postAction(contIndex, action, extra = {}, quiet = false, onError = null) {
    // Compute optimistic patch per action.
    const optimistic = row => {
      if (action === 'mark-reviewed') {
        return { ...row, reviewed: true, reviewedAt: new Date().toISOString(), reviewedBy: 'You' };
      }
      // NLC implies reviewed (mark-nlc writes a signoff server-side). The server then drops the
      // client from this list, so the row disappears on the next refetch (heartbeat: nlc is global).
      if (action === 'mark-nlc') {
        return { ...row, isNlc: true, reviewed: true, reviewedAt: new Date().toISOString(), reviewedBy: 'You' };
      }
      return row;
    };

    // The rendered row = its state before this edit (a setData updater may run later).
    const snapshot = (data?.rows || []).find(r => String(r.contIndex) === String(contIndex));
    patchRow(contIndex, optimistic);

    // nlcExcluded counts this period's marks and is not derivable from this user's rows alone,
    // so we delta-adjust it optimistically alongside the row patch.
    const nlcDelta = action === 'mark-nlc' ? 1 : 0;
    if (nlcDelta) {
      setData(prev => prev
        ? { ...prev, summary: { ...prev.summary, nlcExcluded: prev.summary.nlcExcluded + nlcDelta } }
        : prev);
    }

    try {
      // Queued per client so rapid clicks reach the server in order; the controls stay live meanwhile.
      await mutations.run(contIndex, async () => {
        const res = await fetch(`/api/diStaleClients/${period}`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action, contIndex, ...extra }),
        });
        if (!res.ok) throw new Error(await readError(res));
      }, {
        revert: () => {
          if (snapshot) patchRow(contIndex, () => snapshot);
          if (nlcDelta) {
            setData(prev => prev
              ? { ...prev, summary: { ...prev.summary, nlcExcluded: prev.summary.nlcExcluded - nlcDelta } }
              : prev);
          }
        },
        refetch: silentRefetch,
      });
      if (!quiet) toast.success(`Saved: ${action.replace(/-/g, ' ')}`);
      return true;
    } catch (err) {
      toast.error(`Couldn't save ${action.replace(/-/g, ' ')}: ${err.message}`, { autoClose: 10000 });
      onError?.(err);
      return false;
    }
  }

  async function adminUnmark(contIndex, quiet = false) {
    if (!isAdmin) return;
    if (!quiet && !window.confirm('Unmark this client as reviewed? They will need to review again.')) return;

    const snapshot = (data?.rows || []).find(r => String(r.contIndex) === String(contIndex));
    patchRow(contIndex, r => ({ ...r, reviewed: false, reviewedAt: null, reviewedBy: null }));
    try {
      await mutations.run(contIndex, async () => {
        const res = await fetch(`/api/diAdminUnmark/${period}/stale-client-review`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ recordKey: String(contIndex), role: 'partner' }),
        });
        if (!res.ok) throw new Error(await readError(res));
      }, {
        revert: () => { if (snapshot) patchRow(contIndex, () => snapshot); },
        refetch: silentRefetch,
      });
      if (!quiet) toast.success('Unmarked reviewed.');
      return true;
    } catch (err) {
      toast.error(`Couldn't unmark: ${err.message}`, { autoClose: 10000 });
      return false;
    }
  }

  // Group-level review writes the same per-client sign-offs as the Individual
  // view (one audit_log row per member), so Home / Steering counts are unchanged.
  async function reviewGroup(entity) {
    const pending = entity.members.filter(m => !memberDone(m));
    const results = await Promise.all(pending.map(m => postAction(m.contIndex, 'mark-reviewed', {}, true)));
    const ok = results.filter(Boolean).length;
    if (ok) toast.success(`Marked ${ok} client${ok === 1 ? '' : 's'} reviewed.`);
  }

  async function unmarkGroup(entity) {
    if (!isAdmin) return;
    const done = entity.members.filter(m => m.reviewed && !m.isNlc);
    if (!window.confirm(`Unmark ${done.length} client${done.length === 1 ? '' : 's'} in this group as reviewed?`)) return;
    const results = await Promise.all(done.map(m => adminUnmark(m.contIndex, true)));
    if (results.some(Boolean)) toast.success('Unmarked reviewed.');
  }

  const offices = useMemo(() => {
    if (!data) return [];
    return [...new Set(data.rows.map(r => r.clientOffice))].filter(Boolean).sort();
  }, [data]);

  // Derive the top cards from rows so optimistic updates show instantly.
  // nlcExcluded stays sourced from data.summary (marked this period) but is delta-
  // updated in postAction when this user marks/unmarks NLC.
  const summary = useMemo(() => {
    if (!data) return null;
    const reviewedCount = data.rows.filter(r => r.reviewed).length;
    return {
      totalClients: data.rows.length,
      reviewedCount,
      pendingCount: data.rows.length - reviewedCount,
      nlcExcluded:  data.summary.nlcExcluded,
    };
  }, [data]);

  const entities = useMemo(() => data ? buildEntities(data.rows, groupBy) : [], [data, groupBy]);

  // Status filters apply to the entity: a group is Pending until every member is
  // done and Reviewed once all are reviewed.
  const filtered = useMemo(() => {
    let out = entities;
    if (search) {
      const s = search.toLowerCase();
      out = out.filter(e =>
        e.code.toLowerCase().includes(s) || e.name.toLowerCase().includes(s) ||
        e.members.some(m => m.clientCode.toLowerCase().includes(s) || m.clientName.toLowerCase().includes(s))
      );
    }
    if (officeFilter) out = out.filter(e => e.members.some(m => m.clientOffice === officeFilter));
    // Rows the user just edited stay put even if the edit moved them out of this status.
    const keep = e => e.members.some(m => pinned.has(String(m.contIndex)));
    if (statusFilter === 'pending')  out = out.filter(e => keep(e) || e.members.some(m => !memberDone(m)));
    if (statusFilter === 'reviewed') out = out.filter(e => keep(e) || e.members.every(memberDone));
    return sortDoneLast(out, e => e.members.every(memberDone), e => e.code);
  }, [entities, search, officeFilter, statusFilter, pinned]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage   = Math.min(page, totalPages);
  const paged      = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  if (initialLoading) return <div className="rt-loading">Loading stale clients…</div>;
  if (error && !data) return <div className="rt-error">Error: {error}</div>;
  if (!data)   return null;

  const readOnly = !periodActive;

  // The NLC cell for ONE client. In the group views it still acts on a single client — a group row's switch is
  // for the client whose code the row shows, and each expanded member has its own — never the whole group.
  const nlcCell = (row, inGroup) => (row.isNlc ? (
    <span className="rt-nlc-badge">NLC</span>
  ) : (
    <span title={inGroup ? `Marks only ${row.clientCode} as No Longer Client — not the rest of the group` : undefined}>
      <ToggleSwitch
        variant="danger"
        checked={false}
        disabled={readOnly || row.reviewed}
        onChange={checked => {
          if (checked) { setNlcError(null); setNlcModalRow(row); }
        }}
      />
    </span>
  ));

  return (
    <div className="rt-page">
      <div className="rt-header">
        <div className="rt-title-block">
          <h2 className="rt-title">Stale Client Review</h2>
          {readOnly && <span className="rt-readonly-badge">Read-only</span>}
          <LastRefreshed at={lastRefreshedAt} freshness={freshness} onRefresh={silentRefetch} />
        </div>
      </div>

      {(() => {
        const totalNow    = summary.totalClients;
        const reviewedNow = summary.reviewedCount;
        const pct = totalNow > 0 ? Math.round((reviewedNow / totalNow) * 100) : 0;
        return (
          <div className="rt-progress-section">
            <div className="rt-progress-header">
              <h3 className="rt-progress-title">Your Progress</h3>
              <span className="rt-progress-label-val">
                <span className="rt-progress-count">{reviewedNow} / {totalNow}</span>
                <span className="rt-progress-divider">—</span>
                {pct}% Complete
              </span>
            </div>
            <div className="rt-progress-track">
              <div
                className="rt-progress-fill"
                style={{
                  width: `${Math.min(100, Math.max(0, pct))}%`,
                  background: pct >= 100 ? '#1a7a4a' : pct >= 50 ? '#e6a817' : '#c0392b',
                }}
              />
            </div>
          </div>
        );
      })()}

      <div className="rt-summary">
        <div className="rt-stat"><span className="rt-stat-val">{summary.totalClients}</span><span className="rt-stat-lbl">Your Clients</span></div>
        <div className="rt-stat rt-stat--done"><span className="rt-stat-val">{summary.reviewedCount}</span><span className="rt-stat-lbl">Reviewed</span></div>
        <div className="rt-stat rt-stat--pending"><span className="rt-stat-val">{summary.pendingCount}</span><span className="rt-stat-lbl">Needs Review</span></div>
        <div className="rt-stat"><span className="rt-stat-val">{summary.nlcExcluded}</span><span className="rt-stat-lbl">NLC (This Period)</span></div>
      </div>

      <div className="rt-filters">
        <input className="rt-filter-input" placeholder="Search code or name…"
               value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} />
        <select className="rt-filter-select" value={officeFilter}
                onChange={e => { setOfficeFilter(e.target.value); setPage(1); }}>
          <option value="">All Offices</option>
          {offices.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
        <select className="rt-filter-select" value={statusFilter}
                onChange={e => { setStatusFilter(e.target.value); setPage(1); }}>
          <option value="">All Statuses</option>
          <option value="pending">Pending</option>
          <option value="reviewed">Reviewed</option>
        </select>
        <div className="rt-seg-wrap">
          <span className="rt-seg-title" id="stale-client-view-label">Show WIP detail by level</span>
          <div className="rt-seg" role="group" aria-labelledby="stale-client-view-label">
            {GROUP_VIEWS.map(v => (
              <button key={v.key} type="button"
                      className={`rt-seg-btn ${groupBy === v.key ? 'active' : ''}`}
                      onClick={() => { setGroupBy(v.key); setPage(1); setExpanded(new Set()); }}>
                {v.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="rt-table-wrap">
        <table className="rt-table">
          <thead>
            <tr>
              <th>Code</th>
              <th>Client</th>
              <th>Office</th>
              <th>Manager</th>
              <th title={WIP_TIP}>WIP 365</th>
              <th title={BILLED_TIP}>Billed 365</th>
              <th>Open Jobs</th>
              <th>{groupBy === 'relationship' ? 'Relationship Group' : 'Billing Group'}</th>
              <th>Reviewed</th>
              <th>NLC</th>
            </tr>
          </thead>
          <tbody>
            {paged.map(entity => {
              if (groupBy !== 'client') {
                const allDone  = entity.members.every(memberDone);
                const doneCnt  = entity.members.filter(memberDone).length;
                const isOpen   = expanded.has(entity.key);
                const anyRev   = entity.members.some(m => m.reviewed && !m.isNlc);
                return (
                  <React.Fragment key={entity.key}>
                    <tr className={allDone ? 'rt-row-reviewed' : ''}>
                      <td>
                        {/* Expand the stale members listed here (n reviewed / m). */}
                        {entity.members.length > 1 && (
                          <button type="button" className="rt-group-toggle"
                                  onClick={() => setExpanded(prev => {
                                    const n = new Set(prev);
                                    if (n.has(entity.key)) n.delete(entity.key); else n.add(entity.key);
                                    return n;
                                  })}>
                            {isOpen ? '▾' : '▸'} {doneCnt}/{entity.members.length}
                          </button>
                        )}{' '}
                        {entity.code}
                      </td>
                      <td>{entity.name}</td>
                      <td>{entity.office}</td>
                      <td>{entity.manager}</td>
                      <td>{money(entity.wip)}</td>
                      <td>{money(entity.billed)}</td>
                      <td>
                        {/* Group rows prefix each job with its client code, since jobs span members. */}
                        <JobsCell jobs={entity.members.length > 1
                          ? entity.members.flatMap(m => m.openJobs.map(j => `${m.clientCode}: ${j}`))
                          : entity.members[0].openJobs} />
                      </td>
                      <td><GroupCell label={entity.group.label} clients={entity.group.clients} /></td>
                      <td>
                        <ToggleSwitch
                          checked={allDone}
                          disabled={readOnly || (allDone && !isAdmin) || (allDone && !anyRev)}
                          onChange={checked => {
                            if (checked && !allDone) reviewGroup(entity);
                            else if (!checked && allDone && isAdmin) unmarkGroup(entity);
                          }}
                        />
                      </td>
                      <td>{nlcCell(entity.lead, entity.members.length > 1)}</td>
                    </tr>
                    {isOpen && entity.members.map(m => (
                      <tr key={m.contIndex} className="rt-row-member">
                        <td>{m.clientCode}</td>
                        <td>{m.clientName}</td>
                        <td>{m.clientOffice}</td>
                        <td>{m.clientManager}</td>
                        <td>
                          {USD.format(num(m.wipLast365))}
                          <LastDate label="Last time" date={m.lastTimeEntry} none="No time" fhp={m.clientOffice === 'FHP'} />
                        </td>
                        <td>
                          {USD.format(num(m.billedLast365))}
                          <LastDate label="Last invoice" date={m.lastInvoiceDate} none="No invoices" fhp={m.clientOffice === 'FHP'} />
                        </td>
                        <td><JobsCell jobs={m.openJobs} /></td>
                        <td />
                        <td>{m.isNlc ? <span className="rt-nlc-badge">NLC</span> : (m.reviewed ? 'Reviewed' : 'Pending')}</td>
                        <td>{nlcCell(m, true)}</td>
                      </tr>
                    ))}
                  </React.Fragment>
                );
              }
              const row = entity.members[0];
              return (
                <tr key={row.contIndex}
                    className={row.isNlc ? 'rt-row-nlc' : (row.reviewed ? 'rt-row-reviewed' : '')}>
                  <td>{row.clientCode}</td>
                  <td>{row.clientName}</td>
                  <td>{row.clientOffice}</td>
                  <td>{row.clientManager}</td>
                  <td>
                    {USD.format(row.wipLast365 || 0)}
                    <LastDate label="Last time" date={row.lastTimeEntry} none="No time" fhp={row.clientOffice === 'FHP'} />
                  </td>
                  <td>
                    {USD.format(row.billedLast365 || 0)}
                    <LastDate label="Last invoice" date={row.lastInvoiceDate} none="No invoices" fhp={row.clientOffice === 'FHP'} />
                  </td>
                  <td><JobsCell jobs={row.openJobs} /></td>
                  <td><GroupCell label={entity.group.label} clients={entity.group.clients} /></td>
                  <td>
                    <ToggleSwitch
                      checked={row.reviewed}
                      disabled={readOnly || row.isNlc || (row.reviewed && !isAdmin)}
                      onChange={checked => {
                        if (checked && !row.reviewed) {
                          postAction(row.contIndex, 'mark-reviewed');
                        } else if (!checked && row.reviewed && isAdmin) {
                          adminUnmark(row.contIndex);
                        }
                      }}
                    />
                  </td>
                  <td>{nlcCell(row, false)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {filtered.length === 0 && (
          <div className="rt-empty">No {groupBy === 'client' ? 'clients' : 'groups'} match.</div>
        )}
      </div>

      {filtered.length > PAGE_SIZE && (
        <div className="rt-pagination">
          <button disabled={safePage <= 1} onClick={() => setPage(1)}>«</button>
          <button disabled={safePage <= 1} onClick={() => setPage(p => p - 1)}>‹</button>
          <span className="rt-page-info">Page {safePage} of {totalPages} ({filtered.length} rows)</span>
          <button disabled={safePage >= totalPages} onClick={() => setPage(p => p + 1)}>›</button>
          <button disabled={safePage >= totalPages} onClick={() => setPage(totalPages)}>»</button>
        </div>
      )}

      {nlcModalRow && (
        <DisengagementModal
          client={{ code: nlcModalRow.clientCode, name: nlcModalRow.clientName }}
          {...nlcModalProps('direct')}
          submitting={nlcSaving}
          error={nlcError}
          onCancel={() => setNlcModalRow(null)}
          onConfirm={async (disengagement) => {
            const row = nlcModalRow;
            setNlcSaving(true);
            setNlcError(null);
            // Optimistic + serialized inside postAction; NLC implies reviewed. Partner-only here, so
            // it marks at once. On failure the row is restored and the modal stays open with its answers.
            const ok = await postAction(row.contIndex, 'mark-nlc', {
              clientCode: row.clientCode,
              clientName: row.clientName,
              disengagement,
            }, false, err => setNlcError(err.message));
            setNlcSaving(false);
            if (!ok) return;
            setNlcModalRow(null);
            // Sweep in any selected related parties (the server judges each against who you are).
            if ((disengagement.relatedParties || []).length) {
              await sweepRelated({
                disengagement, primaryContIndex: row.contIndex, primaryClientCode: row.clientCode, period,
                sourceSectionKey: 'stale-client-review',
              });
              silentRefetch();
            }
          }}
        />
      )}
    </div>
  );
}
